import { useFlags } from 'launchdarkly-react-client-sdk'
import { useMemo } from 'react'
import { resolveMeticulousBooleanFlag } from '../inspector/meticulousInspector'

type FlagConfig = Record<string, { type: string }>
type ExtractFlags<T extends FlagConfig> = Record<keyof T, boolean>

// Every read of a useFlags property is a LaunchDarkly evaluation, so resolve a flag when it is read, never by enumerating.
const withMeticulousOverrides = <TFlags extends object>(flags: TFlags) =>
  new Proxy(flags, {
    get: (target, flagKey, receiver) => {
      const value: unknown = Reflect.get(target, flagKey, receiver)
      return typeof flagKey === 'string' && typeof value === 'boolean' ? resolveMeticulousBooleanFlag(flagKey, value) : value
    },
  })

/**
 * Creates a typed LaunchDarkly feature flags hook from a flags.json config.
 *
 * @example
 * export const useMyFlags = createFlagsHook<typeof import('./flags.json')>()
 */
export const createFlagsHook = <TFlagConfig extends FlagConfig>() => {
  const useTypedFlags = useFlags as () => ExtractFlags<TFlagConfig>

  const useFlagsWithMeticulousOverrides = () => {
    const flags = useTypedFlags()
    return useMemo(() => withMeticulousOverrides(flags), [flags])
  }

  return useFlagsWithMeticulousOverrides
}
